import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { Grid, Tooltip, Stack, Button, ButtonGroup, Box, ListItem, LinearProgress, Theme } from "@mui/material";
import { useTheme } from "@mui/material";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import {
  setSearchedCrewmembers,
  setActiveOpenFlyingPairing,
  setFocusedCrewSchedArea,
  setSelectedCrewEvents,
  setWindowTimezone,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import {
  IAirport,
  IDisplayPairing,
  IOpenFlyingAssignmentRequest,
  IOpenFlyingSettings,
  IPosition,
  OpenFlyingActionType,
} from "@vmosolutions/oc-interfaces";
import { OpenFlyingStatusType, IPairingPosition, ClientSettingName } from "@vmosolutions/oc-interfaces";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { DataGridPro, useGridApiRef, GridToolbarContainer, GridCellParams } from "@mui/x-data-grid-pro";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { formatDateString } from "src/components/DateDisplay";
import { timeScales } from "src/pages/FlightTimeline/TimelineUtils";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { ICrewMember, ICrewPairingMap } from "src/redux/reducers/CrewScheduleReducer";
import { IconButton } from "src/components/IconButton/IconButton";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { IAirportOption } from "src/redux/reducers/StructuresReducer";
import moment, { utc } from "moment-timezone";
import _ from "lodash";

interface ICrewOpenFlightsProps {
  windowId: string;
  openFlightSettings: IOpenFlyingSettings;
  setTabValue: React.Dispatch<React.SetStateAction<string | boolean>>;
}

type OrderedRequest = {
  user_id: string;
  actionedAt: string | Date;
  senority: number;
  /**The mongo _id of the request */
  _id: any;
};

function NoRowsPrompt(): JSX.Element {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      No Records
    </Stack>
  );
}

interface IFlightData {
  id: number;
  pairing: object;
  positionType: string;
  positions: number;
  checkIn: string;
  checkOut: string;
  base: string;
  numRequests: object[];
}

enum filledStatuses {
  UNFILLED = "UN-FILLED",
  FILLED = "FILLED",
}

enum visibleStatuses {
  CREW_VISIBLE = "CREW VISIBLE",
  NON_VISIBLE = "NON-VISIBLE",
  ALL = "ALL",
}

const timelineOpenFlightStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
});

export interface IOpenFlightRequestPairing extends IDisplayPairing {
  openFlyingRequest: any[];
}

/** Search CrewPairingData for pairing and inject open flight requestt*/
export const returnCrewPairingAsOpenFlight = (
  pairingID: string,
  crewPairings: ICrewPairingMap,
  requestIDs: any[]
): IOpenFlightRequestPairing | null => {
  if (!pairingID || !crewPairings || !requestIDs) {
    return null;
  }
  // Search unassigned pairings first in to reduce computation.
  const search = crewPairings["Unassigned"]?.pairings?.find((p) => p._id === pairingID);
  if (search?._id) {
    return { ...search, openFlyingRequest: requestIDs };
  } else {
    let pairingData: any | null = null;
    // Search not found, loop through all crew and exit as soon as found anywhere.
    for (const pairingArrays of Object.values(crewPairings)) {
      pairingArrays?.pairings?.every((p) => {
        if (p._id === pairingID) {
          pairingData = p;
          return false; // exit loop if found and add an openFlyingRequest property so that webgl rendering can handle in a unique fashion.
        }
        return true;
      });
      return pairingData?._id ? { ...pairingData, openFlyingRequest: requestIDs } : null;
    }
    return pairingData?._id ? { ...pairingData, openFlyingRequest: requestIDs } : null;
  }
};

/**
 * Function which applies an offset (in hours) forward or back depending on requirement.
 * @param hourOffset - offset in hours
 * @param isAdded - true for adding to existing time, false for subtracting
 * @returns - Date object in UTC format.
 */
const returnOffsetTime = (hourOffset: number, isAdded?: boolean): Date => {
  const currentTime = moment().utc();
  const applyOffset = hourOffset ?? 0;
  return isAdded
    ? currentTime.endOf("day").add(applyOffset, "hour").toDate()
    : currentTime.startOf("day").subtract(applyOffset, "hour").toDate();
};

/**
 * Function to order open flying requests by either date or seniority depending on settings.
 *  - If sorting by date, sorts by oldest actioned date
 *  - If sorting by seniority, sorts by highest seniority (lowest number)
 * @param pairing - the pairing for which requests are being ordered
 * @param crew - the crew list used to find seniority and position information
 * @param positionTitle - the position title to filter crew requests
 * @param requestSortByDate - boolean indicating whether to sort by date or seniority
 * @returns - ordered list of requests
 */
const buildOrderedRequests = (
  pairing: IOpenFlyingAssignmentRequest,
  crew: Array<ICrewMember>,
  positionTitle: string,
  requestSortByDate: boolean
): OrderedRequest[] => {
  const crewById = new Map(crew.map((member) => [member._id, member]));

  const requests: OrderedRequest[] = pairing.crewRequests
    .filter((req) => req.actionType === OpenFlyingActionType.Requested)
    .filter((req) => {
      const currentCrew = crewById.get(req.user_id);
      if (!currentCrew) return false;
      return currentCrew.crewInfo.positions.some((pos: { title: string }) => pos.title === positionTitle);
    })
    .map((req) => ({
      user_id: req.user_id,
      actionedAt: req.actionedAt,
      senority: crewById.get(req.user_id)?.crewInfo?.masterSeniority,
      _id: req._id,
    }));

  if (requestSortByDate) {
    return _.sortBy(requests, (r) => new Date(r.actionedAt).valueOf());
  }

  return requests.sort((a, b) => (a.senority ?? Number.MAX_SAFE_INTEGER) - (b.senority ?? Number.MAX_SAFE_INTEGER));
};

export const CrewOpenFlights = (props: ICrewOpenFlightsProps): JSX.Element => {
  const { windowId, openFlightSettings, setTabValue } = props;
  const tableMaxHeight = 200;
  const apiRef = useGridApiRef();
  const theme = useTheme();
  const dispatch = useDispatch();

  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const activeOpenFlyingPairing = useSelector(
    (state: IRootState) => state.crewSchedule.windowProperties[windowId]?.openFlying?.pairingID
  );

  const defaultStartDate: Date = useMemo(
    () => returnOffsetTime(openFlightSettings?.crewAssignmentSettings?.defaultStartHoursBack, false),
    [openFlightSettings?.crewAssignmentSettings?.defaultStartHoursBack]
  );
  const defaultEndDate: Date = useMemo(
    () => returnOffsetTime(openFlightSettings?.crewAssignmentSettings?.defaultEndHoursForward, true),
    [openFlightSettings?.crewAssignmentSettings?.defaultEndHoursForward]
  );
  const defaultBase: IAirportOption | null = useMemo(() => {
    if (openFlightSettings.crewAssignmentSettings?.defaultBaseICAO && !!airportOptions) {
      return Object.values(airportOptions).find(
        (o) => o.ident === openFlightSettings?.crewAssignmentSettings?.defaultBaseICAO
      );
    }
    return null;
  }, [openFlightSettings?.crewAssignmentSettings?.defaultBaseICAO, airportOptions]);

  const requestSortByDate: boolean = useMemo(
    () => openFlightSettings?.requestSortByDate ?? false,
    [openFlightSettings?.requestSortByDate]
  );

  // FILTERS
  const [currentFilledStatus, setCurrentFilledStatus] = useState(filledStatuses.UNFILLED);
  const [currentVisibleStatus, setCurrentVisibleStatus] = useState(visibleStatuses.CREW_VISIBLE);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [selectedBase, setSelectedBase] = useState<IAirportOption | null>(defaultBase);
  const currentTimeZone = useMemo(() => {
    return selectedBase?.timezoneId ?? "Etc/UTC";
  }, [selectedBase]);

  // update any defaults that can be modified by open flight settings async call.
  useEffect(() => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setSelectedBase(defaultBase);
  }, [defaultStartDate, defaultEndDate, defaultBase]);

  const resetAllFilters = useCallback(() => {
    setCurrentFilledStatus(filledStatuses.UNFILLED);
    setCurrentVisibleStatus(visibleStatuses.CREW_VISIBLE);
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setSelectedBase(defaultBase);
    dispatch(setSearchedCrewmembers(windowId, []));
    dispatch(setActiveOpenFlyingPairing(windowId, [], null));
    getOpenPairings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEndDate, defaultStartDate, defaultBase, windowId, dispatch]);

  // These 2 state variables store the last API response as well as it's retrieval time.
  const [openFlightData, setOpenFlightData] = useState<IOpenFlyingAssignmentRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastDataRetrieval, setLastDataRetrieval] = useState<Date | null>(null);
  // This variable is used to auto refresh the open flight data if information is stale
  const lastAssignmentDate = useSelector((state: IRootState) => state.crewSchedule.lastOpenFlyingAssignment.assignedAt);

  // Filter available options via base values in data.
  const availableAirportOptions: IAirportOption[] = useMemo(() => {
    const uniqueBaseICAO = [
      ...openFlightData.reduce((a, c) => a.set(c.pairing.baseICAO, c.pairing.baseICAO), new Map()).values(),
    ];
    return Object.values(airportOptions).filter((o) => uniqueBaseICAO.includes(o.ident));
  }, [airportOptions, openFlightData]);

  // Create api call to retrieve open pairings
  const getOpenPairings = useCallback(async (): Promise<void> => {
    if (!startDate || !endDate) {
      return;
    }

    setIsLoading(true);
    const results = await AmplifyAPIWrapper.Get<IOpenFlyingAssignmentRequest[]>(
      `/pairings/openflying/requests?&start=${startDate.valueOf()}&end=${endDate.valueOf()}`
    );
    if (Array.isArray(results) && results?.length > 0) {
      setOpenFlightData(results);
      setLastDataRetrieval(new Date());
    }
    setIsLoading(false);
  }, [startDate, endDate]);

  // Triggered on initial load of the component or when start or end times are modified.
  useEffect(() => {
    getOpenPairings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Triggered if data has already been retrieve once before but open flights have since been assigned to crew and data is now stale.
  useEffect(() => {
    if (!!lastDataRetrieval && !!lastAssignmentDate && lastDataRetrieval < lastAssignmentDate) {
      getOpenPairings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDataRetrieval, lastAssignmentDate]);

  useEffect(() => {
    dispatch(setWindowTimezone(windowId, selectedBase ?? null));
    return () => {
      // on unmount of component, reset all stored redux values for open flying.
      dispatch(setSearchedCrewmembers(windowId, []));
      dispatch(setActiveOpenFlyingPairing(windowId, [], null));
    };
  }, [windowId, dispatch, selectedBase]);

  // Whenever open flight data is retrieve, filter the results based on the assigned filter values in the component
  const flightData = useMemo(() => {
    if (!openFlightData) return [];

    const flightDataArray: any = [];
    const vStatus = currentVisibleStatus;
    const fStatus = currentFilledStatus;

    openFlightData
      ?.filter((p) => !selectedBase?.ident || p.pairing.baseICAO === selectedBase?.ident)
      ?.forEach((pairing: IOpenFlyingAssignmentRequest) => {
        const parentPairing: { [key: string]: any } = {};
        pairing.pairing.positions.forEach((openPosition: any) => {
          if (
            openPosition.openFlyingStatus !== OpenFlyingStatusType.Closed &&
            ((fStatus === filledStatuses.UNFILLED && !openPosition.crew) ||
              (fStatus === filledStatuses.FILLED && openPosition.crew)) &&
            (vStatus === visibleStatuses.ALL ||
              (vStatus === visibleStatuses.CREW_VISIBLE &&
                openPosition.openFlyingStatus == OpenFlyingStatusType.OpenVisible) ||
              (vStatus === visibleStatuses.NON_VISIBLE &&
                openPosition.openFlyingStatus == OpenFlyingStatusType.OpenNonVisible))
          ) {
            if (parentPairing[openPosition?.position?.title])
              parentPairing[openPosition.position.title].push(openPosition);
            else parentPairing[openPosition.position.title] = [openPosition];
          }
        });
        //filter requests by whether the action type is "requested" and whether they qualify
        //for this particular position
        const validRequests = pairing.crewRequests.filter((req: any, index: any) => {
          return req.actionType === "requested";
        });
        //used for filtering tests while no actual requests present in env
        for (const [key, value] of Object.entries(parentPairing)) {
          flightDataArray.push({
            id: flightDataArray.length,
            pairing: pairing.pairing,
            positionType: key,
            positions: value,
            checkIn: pairing.pairing.approxStart,
            checkOut: pairing.pairing.approxEnd,
            base: pairing.pairing.baseICAO,
            numRequests: buildOrderedRequests(pairing, crew, key, requestSortByDate),
          });
        }
      });
    return flightDataArray;
  }, [crew, currentFilledStatus, currentVisibleStatus, openFlightData, selectedBase, requestSortByDate]);

  const toggleActiveOpenFlyingPairing = useCallback(
    (params: any, ctrlKey: boolean): void => {
      const pairingID = params?.row?.pairing?._id;
      const requestIDs = params?.row?.numRequests ?? [];
      // If user ctrl+clicked a row that was already actively selected, unset it and remove crew filter.
      const unsetFilter = activeOpenFlyingPairing === pairingID && ctrlKey;
      const requestorIDs: string[] =
        params?.row?.numRequests?.length > 0 && !unsetFilter
          ? params?.row?.numRequests.map((req: any) => {
              return req.user_id;
            })
          : [];
      // Filter crew schedule crew list to requestors.
      dispatch(setSearchedCrewmembers(windowId, requestorIDs));
      // Toggle active open flying pairing for window.
      dispatch(setActiveOpenFlyingPairing(windowId, requestIDs, !unsetFilter ? pairingID : null));
      if (!unsetFilter && params?.row?.pairing?.approxStart && params?.row?.pairing?.approxEnd) {
        const start = new Date(params?.row?.pairing?.approxStart);
        const end = new Date(params?.row?.pairing?.approxEnd);
        // Deselect any previously selected events;
        dispatch(setSelectedCrewEvents([], false, false, false));
        dispatch(
          setFocusedCrewSchedArea(
            {
              timeInterval: [start.valueOf() - timeScales.day, end.valueOf() + timeScales.day],
              crewIds: requestorIDs,
            },
            windowId
          )
        );
      }
    },
    [dispatch, windowId, activeOpenFlyingPairing]
  );

  const customToolbar = (): JSX.Element => {
    return (
      <GridToolbarContainer sx={{ display: "flex", p: 1, m: 0 }}>
        <IconButton style={{ paddingBottom: "4px", marginRight: "15px" }} onClick={() => setTabValue(false)}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
        <Button color="secondary" variant="contained" onClick={() => resetAllFilters()}>
          Reset
        </Button>

        <ButtonGroup>
          <Button
            sx={{ margin: "0 !important" }}
            variant={currentFilledStatus === filledStatuses.UNFILLED ? "contained" : "outlined"}
            onClick={(e) => setCurrentFilledStatus(filledStatuses.UNFILLED)}
          >
            UN-FILLED
          </Button>
          <Button
            sx={{ margin: "0 !important" }}
            variant={currentFilledStatus === filledStatuses.FILLED ? "contained" : "outlined"}
            onClick={(e) => setCurrentFilledStatus(filledStatuses.FILLED)}
          >
            FILLED
          </Button>
        </ButtonGroup>

        <ButtonGroup>
          <Button
            sx={{ margin: "0 !important" }}
            variant={currentVisibleStatus === visibleStatuses.CREW_VISIBLE ? "contained" : "outlined"}
            onClick={(e) => setCurrentVisibleStatus(visibleStatuses.CREW_VISIBLE)}
          >
            CREW VISIBLE
          </Button>
          <Button
            sx={{ margin: "0 !important" }}
            variant={currentVisibleStatus === visibleStatuses.NON_VISIBLE ? "contained" : "outlined"}
            onClick={(e) => setCurrentVisibleStatus(visibleStatuses.NON_VISIBLE)}
          >
            NON-VISIBLE
          </Button>
          <Button
            sx={{ margin: "0 !important" }}
            variant={currentVisibleStatus === visibleStatuses.ALL ? "contained" : "outlined"}
            onClick={(e) => setCurrentVisibleStatus(visibleStatuses.ALL)}
          >
            ALL
          </Button>
        </ButtonGroup>

        <Grid sx={{ display: "flex", flexDirection: "row", justifyContent: "start" }}>
          <SelectField
            label="Base Filter"
            name="Base"
            options={availableAirportOptions}
            getOptionLabel={(airport) => `${airport.ident} - ${airport.name}`}
            getOptionValue={(airport) => airport.timezoneId}
            value={selectedBase}
            onChange={setSelectedBase}
            maxMenuHeight={400}
            style={{ width: "250px" }}
            isClearable={true}
          />
          <DateTimeField
            label="Start"
            name="start"
            value={startDate.valueOf()}
            maxDate={endDate.valueOf()}
            onChange={(date) => setStartDate(new Date(date))}
            isEpoch={true}
            labelStyle={{ width: "50px !important" }}
          />
          <DateTimeField
            label="End"
            name="end"
            value={endDate.valueOf()}
            minDate={startDate.valueOf()}
            onChange={(date) => setEndDate(new Date(date))}
            isEpoch={true}
            labelStyle={{ width: "auto !important" }}
          />
        </Grid>
      </GridToolbarContainer>
    );
  };

  return (
    <Grid container sx={timelineOpenFlightStyles(theme)}>
      <Grid item xs={12} sx={{ overFlowY: "scroll", height: `calc(${tableMaxHeight}px + 115px)` }}>
        <DataGridPro
          initialState={{
            columns: {
              columnVisibilityModel: {
                shortCode: true,
                positions: true,
                checkIn: true,
                checkOut: true,
                base: true,
                numRequests: true,
              },
            },
            sorting: {
              sortModel: [{ field: "checkIn", sort: "desc" }],
            },
          }}
          columns={[
            {
              flex: 0.1,
              field: "pairing",
              headerName: "Short Code",
              type: "string",
              renderCell: (params: GridCellParams) => {
                return (
                  <ShortCodeChip
                    code={params.row.pairing.shortId}
                    itemId={params.row.pairing._id}
                    type="pairing"
                    edit={true}
                  />
                );
              },
            },
            {
              flex: 0.1,
              field: "positions",
              headerName: "Positions",
              type: "string",
              renderCell: (params: GridCellParams) => {
                return (
                  <Box sx={{ display: "inline-flex" }}>
                    <PositionBadge
                      className="crew_badge"
                      positionTitle={params.row.positionType}
                      filled={currentFilledStatus === filledStatuses.FILLED}
                      count={params.row.positions.length}
                    />
                  </Box>
                );
              },
              sortComparator: (v1, v2) => {
                return v2[0].position.displayOrder - v1[0].position.displayOrder;
              },
            },
            {
              flex: 0.25,
              field: "checkIn",
              headerName: "Check In",
              type: "string",
              renderCell: (params: GridCellParams) => {
                return formatDateString(params.row.checkIn, currentTimeZone, true);
              },
            },
            {
              flex: 0.25,
              field: "checkOut",
              headerName: "Check Out",
              type: "string",
              renderCell: (params: GridCellParams) => {
                return formatDateString(params.row.checkOut, currentTimeZone, true);
              },
            },
            {
              flex: 0.15,
              field: "base",
              headerName: "Base",
              type: "string",
            },
            {
              flex: 0.15,
              field: "numRequests",
              headerName: "Requests",
              type: "number",
              sortComparator: (a, b) => (Array.isArray(a) ? a.length : 0) - (Array.isArray(b) ? b.length : 0),
              renderCell: (params: GridCellParams) => {
                const requests = Array.isArray(params.value) ? params.value : [];

                if (requests.length === 0) {
                  return <Box>{0}</Box>;
                }

                const tooltipContent = (
                  <Box sx={{ position: "relative", pr: 2 }}>
                    {requests.length > 1 && (
                      <Tooltip
                        title={requestSortByDate ? "Sorted by oldest request date" : "Sorted by seniority"}
                        placement="top"
                        enterDelay={250}
                        enterNextDelay={250}
                        leaveDelay={400}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 6,
                            fontSize: 14,
                            opacity: 0.6,
                            cursor: "help",
                          }}
                        >
                          {requestSortByDate ? "↓" : "↑"}
                        </Box>
                      </Tooltip>
                    )}

                    {requests.map((req: any, index: number) => {
                      const crewMember = crew.find((member) => member._id === req.user_id);
                      if (!crewMember) return null;

                      return (
                        <Tooltip
                          key={index}
                          title={`Requested ${formatDateString(req.actionedAt, currentTimeZone, true)}`}
                          placement="right"
                          enterDelay={250}
                          enterNextDelay={250}
                          leaveDelay={400}
                        >
                          <ListItem
                            sx={{
                              py: 0.25,
                              cursor: "default",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {crewMember.firstName} {crewMember.lastName}
                          </ListItem>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );

                return (
                  <Tooltip
                    title={tooltipContent}
                    placement="right"
                    enterDelay={250}
                    enterNextDelay={250}
                    leaveDelay={800}
                    PopperProps={{
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, 6],
                          },
                        },
                      ],
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "default",
                      }}
                    >
                      {requests.length}
                    </Box>
                  </Tooltip>
                );
              },
            },
          ]}
          apiRef={apiRef}
          checkboxSelection={false}
          hideFooterSelectedRowCount
          sortingOrder={["desc", "asc"]}
          disableMultipleRowSelection
          pagination
          autoPageSize
          onRowClick={(params, event, details) => {
            toggleActiveOpenFlyingPairing(params, event?.ctrlKey);
          }}
          density="compact"
          slots={{ toolbar: customToolbar, noRowsOverlay: NoRowsPrompt }}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
          }}
          rows={flightData}
        />
      </Grid>
    </Grid>
  );
};
